import { FC, useCallback, useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { ToastContainer } from 'react-toastify'
import { RentalListTable } from './RentalListTable'
import { initialRentFilter, IRentFilterModel, IRentModel, validationRentFilter } from './RentModel'
import { filterRents } from './Api'
import { showErrorToast } from '../../../shared/components/ToastUtils'
import AsyncSearchInput from '../../../shared/components/AsyncSearchInput'
import TextInput from '../../../shared/components/TextInput'
import SelectInput from '../../../shared/components/SelectInput'
import DateInput from '../../../shared/components/DateInput'
import { AsyncCarModel, AsyncCustomerModel } from '../../../shared/models/ApiModels'
import {
  branches,
  fetchAsyncCarData,
  fetchAsyncCustomerData,
  personels,
  reservationSources,
  vehicleGroups,
} from '../../../shared/models/SharedApiLookups'
import {
  BALANCE_FILTER,
  DOCUMENT_STATUS_FILTER,
  INVOICE_STATUS_FILTER,
  PERSONNEL_FILTER_ROLE,
  PLATE_SEARCH_MODE,
  RAC_TABLET_USAGE,
  RENTAL_LIST_DATE_FILTER_TYPE,
  RENTAL_TYPE_FILTER,
  RENT_LIST_STATUS,
  RENT_OFFICE_FILTER_TYPE,
  SUMMARY_INFO_FILTER,
} from './RentalListLookups'
import MultiSelectCombobox from '../../../shared/components/MultiSelectCombobox'

type RentalListFormValues = Omit<IRentFilterModel, 'dateFilterType' | 'officeFilterType'> & {
  dateFilterType: string | null
  officeFilterType: string | null
}

function toRentFilter(f: RentalListFormValues): IRentFilterModel {
  return {
    ...f,
    dateFilterType:
      f.dateFilterType != null && f.dateFilterType !== '' ? Number(f.dateFilterType) : null,
    officeFilterType:
      f.officeFilterType != null && f.officeFilterType !== '' ? Number(f.officeFilterType) : null,
    officeIdList: Array.isArray(f.officeIdList) ? f.officeIdList : [],
    reservationSourceIdList: Array.isArray(f.reservationSourceIdList)
      ? f.reservationSourceIdList
      : [],
    vehicleGroupIdList: Array.isArray(f.vehicleGroupIdList) ? f.vehicleGroupIdList : [],
    plateText: f.plateText?.trim() ? f.plateText.trim() : null,
  }
}

const initialRentalListForm: RentalListFormValues = {
  ...initialRentFilter,
  dateFilterType: '1',
  officeFilterType: '0',
}

export const RentalListPage: FC = () => {
  const [rows, setRows] = useState<IRentModel[]>([])
  const [branchOptions, setBranchOptions] = useState<{ value: string; label: string }[]>([])
  const [vehicleGroupOptions, setVehicleGroupOptions] = useState<{ value: string; label: string }[]>(
    []
  )
  const [reservationSourceOptions, setReservationSourceOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [personnelOptions, setPersonnelOptions] = useState<{ value: string; label: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [pageSize] = useState(10)
  const [filterValues, setFilterValues] = useState<IRentFilterModel>(toRentFilter(initialRentalListForm))
  const [filterOpen, setFilterOpen] = useState(true)
  const [detailOpen, setDetailOpen] = useState(false)

  useEffect(() => {
    Promise.all([branches(), vehicleGroups(), reservationSources(), personels()]).then(
      ([br, vg, rs, pe]) => {
        setBranchOptions(br)
        setVehicleGroupOptions(vg)
        setReservationSourceOptions(rs)
        setPersonnelOptions(pe)
      }
    )
  }, [])

  const filter = useCallback(
    async (
      values: IRentFilterModel,
      page: number = 0,
      sort?: string,
      order?: 'asc' | 'desc'
    ) => {
      setLoading(true)
      try {
        const res = await filterRents(values, page, pageSize, sort, order)
        const data = res.data.payload || res.data
        setRows(data?.content ?? [])
        setTotalPages(data?.totalPages ?? 1)
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Kira listesi yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    [pageSize]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        filter(filterValues, newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, filter, filterValues]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      filter(filterValues, currentPage, column, order)
    },
    [currentPage, filter, filterValues]
  )

  const handleFilter = (values: RentalListFormValues) => {
    const payload = toRentFilter(values)
    setFilterValues(payload)
    setCurrentPage(0)
    filter(payload, 0, sortBy, sortOrder)
  }

  return (
    <>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex align-items-center gap-2 my-1'>
              <i className='bi bi-card-list fs-1 text-primary' aria-hidden />
              <h3 className='fw-bold m-0'>Kira Listeleme</h3>
            </div>
          </div>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFilterOpen((p) => !p)}
              aria-expanded={filterOpen}
            >
              <i className={`ki-duotone ki-${filterOpen ? 'up' : 'down'} fs-2`}>
                <span className='path1' />
                <span className='path2' />
              </i>
              {filterOpen ? 'Filtreleri gizle' : 'Filtreleri göster'}
            </button>
          </div>
        </div>

        <div className={`collapse ${filterOpen ? 'show' : ''}`}>
          <div className='card-body border-top pt-6'>
            <Formik
              initialValues={initialRentalListForm}
              validationSchema={validationRentFilter}
              onSubmit={handleFilter}
            >
              {(formik) => (
                <Form className='form'>
                  <div className='row g-5 mb-5'>
                    <AsyncSearchInput<AsyncCustomerModel>
                      name='customer'
                      placeholder='Ad-Soyad için arayın'
                      fetchHandler={fetchAsyncCustomerData}
                      displayField={(item) => `${item.firstName} ${item.lastName}`}
                      onSelect={(item) => formik.setFieldValue('customerId', item.id)}
                      onClear={() => formik.setFieldValue('customerId', null)}
                      wrapperClassName='col-lg-6 col-md-6'
                    />

                    <TextInput
                      name='reservationNo'
                      placeholder='Sözleşme no giriniz'
                      wrapperClassName='col-lg-2 col-md-6'
                    />
                    <TextInput name='fileNo' placeholder='Dosya No giriniz' wrapperClassName='col-lg-2 col-md-6' />
                  </div>

                  <div className='row g-5 mb-5'>
                    <AsyncSearchInput<AsyncCustomerModel>
                      name='customerCompany'
                      placeholder='Cari bilgi — aramaya başlayın'
                      fetchHandler={fetchAsyncCustomerData}
                      displayField={(item) => `${item.firstName} ${item.lastName}`}
                      onSelect={(item) => formik.setFieldValue('customerCompanyId', item.id)}
                      onClear={() => formik.setFieldValue('customerCompanyId', null)}
                      wrapperClassName='col-lg-6 col-md-12'
                    />
                    <SelectInput
                      name='rentListStatus'
                      wrapperClassName='col-lg-2 col-md-6'
                      options={[...RENT_LIST_STATUS]}
                      firstItemLabel='Kira Durumu Seçiniz'
                    />
                    <SelectInput
                      name='invoiceStatus'
                      wrapperClassName='col-lg-2 col-md-6'
                      options={[...INVOICE_STATUS_FILTER]}
                      firstItemLabel='Fatura Durumu Seçiniz'
                    />
                    <SelectInput
                      name='summaryInfo'
                      wrapperClassName='col-lg-2 col-md-6'
                      options={[...SUMMARY_INFO_FILTER]}
                      firstItemLabel='Özet Bilgi Seçiniz'
                    />
                  </div>
                  <div className='row g-5 mb-5'>
                    <SelectInput
                      name='plateSearchMode'
                      wrapperClassName='col-lg-3 col-md-6'
                      options={[...PLATE_SEARCH_MODE]}
                      firstItemLabel='Plaka Arama Modu Seçiniz'
                    />
                    <TextInput
                      name='plateText'
                      placeholder='Plaka yazınız'
                      wrapperClassName='col-lg-3 col-md-6'
                    />
                    <AsyncSearchInput<AsyncCarModel>
                      name='car'
                      placeholder='Plaka ile araç seçin'
                      fetchHandler={fetchAsyncCarData}
                      displayField={(item) => `${item.plate}`}
                      onSelect={(item) => formik.setFieldValue('carId', item.id)}
                      onClear={() => formik.setFieldValue('carId', null)}
                      wrapperClassName='col-lg-6 col-md-6'
                    />

                  </div>

                  <div className='row g-5 mb-5'>
                    <SelectInput
                      name='dateFilterType'
                      wrapperClassName='col-lg-2 col-md-6'
                      options={[...RENTAL_LIST_DATE_FILTER_TYPE]}
                      firstItemLabel='Tarih Türü Seçiniz'
                    />
                    <DateInput name='startDate' type='date' wrapperClassName='col-lg-2 col-md-6' />
                    <DateInput name='endDate' type='date' wrapperClassName='col-lg-2 col-md-6' />
                    <SelectInput
                      name='officeFilterType'
                      wrapperClassName='col-lg-2 col-md-6'
                      options={[...RENT_OFFICE_FILTER_TYPE]}
                      firstItemLabel='Ofis Seçiniz'
                    />
                    <MultiSelectCombobox
                      name='officeIdList'
                      wrapperClassName='col-lg-4 col-md-6'
                      options={branchOptions}
                      placeholder='Ofisleri seçiniz'
                    />

                  </div>

                  <div className='row g-5 mb-5'>
                    <MultiSelectCombobox
                      name='vehicleGroupIdList'
                      wrapperClassName='col-lg-6 col-md-6'
                      options={vehicleGroupOptions}
                      placeholder='Araç gruplarını seçiniz'
                    />
                    <MultiSelectCombobox
                      name='reservationSourceIdList'
                      wrapperClassName='col-lg-6 col-md-6'
                      options={reservationSourceOptions}
                      placeholder='Rezervasyon kaynağını seçiniz'
                    />
                  </div>
                  <div className='row g-5 mb-5'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      onClick={() => setDetailOpen((d) => !d)}
                    >
                      <i className='bi bi-sliders me-1' aria-hidden />
                      {detailOpen ? 'Detayları gizle' : 'Detaylar'}
                    </button>
                  </div>
                  <div className={`collapse ${detailOpen ? 'show' : ''}`}>
                    <div className='row g-5 mb-5 border rounded p-4 bg-light'>
                      <SelectInput
                        name='racTabletUsage'
                        label='Rac tablet kullanım'
                        wrapperClassName='col-lg-2 col-md-6'
                        options={[...RAC_TABLET_USAGE]}
                      />
                      <SelectInput
                        name='documentStatus'
                        label='Evrak durumu'
                        wrapperClassName='col-lg-2 col-md-6'
                        options={[...DOCUMENT_STATUS_FILTER]}
                        firstItemLabel='Seçiniz'
                      />
                      <SelectInput
                        name='rentalTypeFilter'
                        label='Kiralama türü'
                        wrapperClassName='col-lg-2 col-md-6'
                        options={[...RENTAL_TYPE_FILTER]}
                        firstItemLabel='Seçiniz'
                      />
                      <SelectInput
                        name='personnelFilterType'
                        label='Personel rolü'
                        wrapperClassName='col-lg-3 col-md-6'
                        options={[...PERSONNEL_FILTER_ROLE]}
                        firstItemLabel='Seçiniz'
                      />
                      <SelectInput
                        name='personnelId'
                        label='Personel'
                        wrapperClassName='col-lg-3 col-md-6'
                        options={personnelOptions}
                        firstItemLabel='Seçiniz'
                      />
                      <SelectInput
                        name='balanceFilter'
                        label='Bakiye / fatura'
                        wrapperClassName='col-lg-2 col-md-6'
                        options={[...BALANCE_FILTER]}
                      />
                      <TextInput
                        name='cardFirst4'
                        label='Kart No — İlk 4'
                        placeholder='0000'
                        wrapperClassName='col-lg-2 col-md-6'
                      />
                      <TextInput
                        name='cardLast4'
                        label='Kart No — Son 4'
                        placeholder='0000'
                        wrapperClassName='col-lg-2 col-md-6'
                      />
                    </div>
                  </div>


                  <div className='d-flex flex-wrap align-items-center gap-4'>
                    <button type='submit' className='btn btn-primary' disabled={loading}>
                      {loading ? (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                          Lütfen bekleyin...
                          <span className='spinner-border spinner-border-sm align-middle ms-2' />
                        </span>
                      ) : (
                        <>
                          <i className='bi bi-funnel me-2' aria-hidden />
                          Filtrele
                        </>
                      )}
                    </button>

                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      onClick={() => formik.resetForm({ values: initialRentalListForm })}
                    >
                      Temizle
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        <div className='card-body pt-0'>
          <RentalListTable
            data={rows}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={loading}
          />
        </div>
      </div>

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}
